// src/components/HR/EmployeeRegistrationSection.js

import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useAppContext } from '../../App';
import { api, BASE_URL, resolveFileUrl } from '../../apiService';
import { toast } from 'react-toastify';
import ConfirmationModal from '../Common/ConfirmationModal';

// --- Internal Component: EmployeeForm ---
function EmployeeForm({ employeeToEdit, onBack }) {
    const { allStations, currentStationId } = useAppContext(); 
    const toDateInputValue = (value) => {
        if (!value) return '';
        if (typeof value === 'string') {
            const trimmed = value.trim();
            if (!trimmed) return '';
            if (trimmed.length >= 10) return trimmed.slice(0, 10);
        }
        const d = value instanceof Date ? value : new Date(value);
        if (Number.isNaN(d.getTime())) return '';
        return d.toISOString().slice(0, 10);
    };
    const formatPhone = (value) => {
        const digits = String(value || '').replace(/\D/g, '').slice(0, 11);
        if (digits.length <= 4) return digits;
        return `${digits.slice(0, 4)}-${digits.slice(4)}`;
    };
    const formatCnic = (value) => {
        const digits = String(value || '').replace(/\D/g, '').slice(0, 13);
        if (digits.length <= 5) return digits;
        if (digits.length <= 12) return `${digits.slice(0, 5)}-${digits.slice(5)}`;
        return `${digits.slice(0, 5)}-${digits.slice(5, 12)}-${digits.slice(12)}`;
    };
    
    const [formData, setFormData] = useState({
        fullName: '', fatherName: '', cnic: '', dob: '', employeeId: '',
        designation: '', stationId: currentStationId, joiningDate: '', phone: '', email: '', salary: '',
        dutyShifts: [{ start: '09:00', end: '17:00' }]
    });
    const isEmailValid = useMemo(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(formData.email || '').trim()), [formData.email]);
    const [photoFile, setPhotoFile] = useState(null);
    const [photoPreview, setPhotoPreview] = useState('https://placehold.co/192x192/E2E8F0/4A5568?text=Photo');
    const [cvFile, setCvFile] = useState(null);
    const [idCardFrontFile, setIdCardFrontFile] = useState(null);
    const [idCardBackFile, setIdCardBackFile] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [modelsLoaded, setModelsLoaded] = useState(false);
    const [existingCvURL, setExistingCvURL] = useState('');
    const [existingIdCardFrontURL, setExistingIdCardFrontURL] = useState('');
    const [existingIdCardBackURL, setExistingIdCardBackURL] = useState('');

    useEffect(() => {
        const loadModels = async () => {
            if (modelsLoaded || !window.faceapi) return;
            toast.info("Loading AI models, please wait...", { autoClose: 2500, toastId: 'loading-models-form' });
            try {
                if (window.tf && typeof window.tf.setBackend === 'function') {
                    try {
                        await window.tf.setBackend('webgl');
                        if (typeof window.tf.ready === 'function') await window.tf.ready();
                    } catch {
                        await window.tf.setBackend('cpu');
                        if (typeof window.tf.ready === 'function') await window.tf.ready();
                    }
                }
                const MODEL_URL = 'https://cdn.jsdelivr.net/gh/justadudewhohacks/face-api.js@0.22.2/weights';
                await Promise.all([
                    window.faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
                    window.faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
                    window.faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL)
                ]);
                setModelsLoaded(true);
                toast.dismiss('loading-models-form');
                toast.success("AI models are ready!");
            } catch (e) {
                toast.dismiss('loading-models-form');
                toast.error("AI models could not be loaded. Please check your internet connection.");
            }
        };
        if (window.faceapi) { loadModels(); } 
        else {
            const timer = setTimeout(() => { if(window.faceapi) loadModels(); }, 1000);
            return () => clearTimeout(timer);
        }
    }, [modelsLoaded]);

    useEffect(() => {
        if (employeeToEdit) {
            setFormData({
                fullName: employeeToEdit.fullName || '', fatherName: employeeToEdit.fatherName || '',
                cnic: employeeToEdit.cnic || '', dob: toDateInputValue(employeeToEdit.dob),
                employeeId: employeeToEdit.employeeId || '', designation: employeeToEdit.designation || '',
                stationId: employeeToEdit.stationId || '', joiningDate: toDateInputValue(employeeToEdit.joiningDate),
                phone: employeeToEdit.phone || '', email: employeeToEdit.email || '', salary: employeeToEdit.salary || '',
                dutyShifts: employeeToEdit.dutyShifts && employeeToEdit.dutyShifts.length > 0 ? employeeToEdit.dutyShifts : [{ start: '09:00', end: '17:00' }]
            });
            if (employeeToEdit.photoURL) {
                setPhotoPreview(`${BASE_URL}${employeeToEdit.photoURL}`);
            }
            setExistingCvURL(employeeToEdit.cvURL || '');
            setExistingIdCardFrontURL(employeeToEdit.idCardFrontURL || '');
            setExistingIdCardBackURL(employeeToEdit.idCardBackURL || '');
        } else {
            const prefix = 'EMP-';
            const timestamp = Date.now().toString().slice(-6);
            setFormData(prev => ({ ...prev, employeeId: prefix + timestamp, stationId: currentStationId }));
            setExistingCvURL('');
            setExistingIdCardFrontURL('');
            setExistingIdCardBackURL('');
        }
        setCvFile(null);
        setIdCardFrontFile(null);
        setIdCardBackFile(null);
    }, [employeeToEdit, currentStationId]);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setPhotoFile(file);
            setPhotoPreview(URL.createObjectURL(file));
        }
    };
    
    const handleCvChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (file.type !== 'application/pdf') {
            toast.error("CV must be a PDF file.");
            e.target.value = '';
            return;
        }
        setCvFile(file);
    };

    const handleIdCardFrontChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            toast.error("ID Card Front must be an image.");
            e.target.value = '';
            return;
        }
        setIdCardFrontFile(file);
    };

    const handleIdCardBackChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            toast.error("ID Card Back must be an image.");
            e.target.value = '';
            return;
        }
        setIdCardBackFile(file);
    };

    const handleChange = (e) => {
        const { id, value } = e.target;
        if (id === 'phone') {
            setFormData(prev => ({ ...prev, phone: formatPhone(value) }));
            return;
        }
        if (id === 'cnic') {
            setFormData(prev => ({ ...prev, cnic: formatCnic(value) }));
            return;
        }
        setFormData(prev => ({ ...prev, [id]: value }));
    };
    
    const handleShiftChange = (index, field, value) => {
        const newShifts = [...formData.dutyShifts];
        newShifts[index][field] = value;
        setFormData(prev => ({...prev, dutyShifts: newShifts}));
    };

    const addShift = () => setFormData(prev => ({...prev, dutyShifts: [...prev.dutyShifts, {start: '', end: ''}]}));
    
    const removeShift = (index) => {
        if (formData.dutyShifts.length <= 1) return;
        setFormData(prev => ({...prev, dutyShifts: formData.dutyShifts.filter((_, i) => i !== index)}));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!employeeToEdit && !photoFile) { toast.error("Employee photo is required for registration."); return; }
        if (!employeeToEdit && !cvFile) { toast.error("CV (PDF) is required for registration."); return; }
        if (!employeeToEdit && !idCardFrontFile) { toast.error("ID Card Front is required for registration."); return; }
        if (!employeeToEdit && !idCardBackFile) { toast.error("ID Card Back is required for registration."); return; }
        if (employeeToEdit && !existingCvURL && !cvFile) { toast.error("CV (PDF) is required."); return; }
        if (employeeToEdit && !existingIdCardFrontURL && !idCardFrontFile) { toast.error("ID Card Front is required."); return; }
        if (employeeToEdit && !existingIdCardBackURL && !idCardBackFile) { toast.error("ID Card Back is required."); return; }
        if (!formData.stationId) { toast.error("Station is required."); return; }
        if (!formData.fullName || !formData.fatherName || !formData.cnic || !formData.dob || !formData.designation || !formData.joiningDate || !formData.phone || !formData.email || !formData.salary) {
            toast.error("Please fill all required fields.");
            return;
        }
        if (formData.dutyShifts.some(s => !s.start || !s.end)) {
            toast.error("Please complete all duty shifts.");
            return;
        }
        if (!/^\d{5}-\d{7}-\d$/.test(formData.cnic)) {
            toast.error("CNIC format must be 35202-8486355-7");
            return;
        }
        if (!/^03\d{2}-\d{7}$/.test(formData.phone)) {
            toast.error("Phone format must be 0314-5505585");
            return;
        }
        setIsSubmitting(true);
        if (photoFile) toast.info("Processing photo...");

        try {
            let photoURL = employeeToEdit?.photoURL || '';
            let faceDescriptorForDb = employeeToEdit?.faceDescriptor || null;
            let cvURL = existingCvURL;
            let idCardFrontURL = existingIdCardFrontURL;
            let idCardBackURL = existingIdCardBackURL;

            if (photoFile) {
                const photoFormData = new FormData();
                photoFormData.append('photo', photoFile);

                const response = await fetch(`${BASE_URL}/api/upload`, {
                    method: 'POST',
                    body: photoFormData,
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.error || 'Photo upload failed');
                }

                const result = await response.json();
                photoURL = result.filePath;
            }
            
            const uploadGenericFile = async (file) => {
                const fd = new FormData();
                fd.append('photo', file);
                const response = await fetch(`${BASE_URL}/api/upload`, { method: 'POST', body: fd });
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.error || 'File upload failed');
                }
                return response.json();
            };
            
            if (cvFile) {
                const result = await uploadGenericFile(cvFile);
                cvURL = result.filePath;
            }
            if (idCardFrontFile) {
                const result = await uploadGenericFile(idCardFrontFile);
                idCardFrontURL = result.filePath;
            }
            if (idCardBackFile) {
                const result = await uploadGenericFile(idCardBackFile);
                idCardBackURL = result.filePath;
            }

            const workerData = {
                ...formData,
                dob: toDateInputValue(formData.dob),
                joiningDate: toDateInputValue(formData.joiningDate),
                salary: parseFloat(formData.salary) || 0,
                photoURL, faceDescriptor: faceDescriptorForDb,
                cvURL,
                idCardFrontURL,
                idCardBackURL,
                updatedAt: new Date().toISOString().slice(0, 19).replace('T', ' '),
            };
            
            if (!employeeToEdit) workerData.createdAt = new Date().toISOString().slice(0, 19).replace('T', ' ');
            
            if (employeeToEdit) {
                await api.put('workers', employeeToEdit.id, workerData);
            } else {
                await api.post('workers', workerData);
            }
            toast.success(`Employee ${employeeToEdit ? 'updated' : 'registered'} successfully!`);
            onBack();
        } catch (err) {
            toast.error(`Operation failed: ${err.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
      <div className="space-y-6">
          <div className="mb-8">
            <button onClick={onBack} className="text-rose-600 font-semibold flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                Back to Employee List
            </button>
            <h1 className="text-4xl font-bold text-gray-800 mt-2">{employeeToEdit ? 'Edit Employee' : 'New Employee Registration'}</h1>
          </div>
          <form onSubmit={handleSubmit} className="bg-white p-8 rounded-2xl shadow-lg">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  <div className="md:col-span-1 flex flex-col items-center">
                      <h2 className="text-xl font-bold text-gray-700 mb-4 w-full">Employee's Photo</h2>
                      <div className="w-48 h-48 bg-gray-200 rounded-full mb-4 flex items-center justify-center overflow-hidden">
                          <img id="photo-preview" src={photoPreview} className="w-full h-full object-cover" alt="Employee"/>
                      </div>
                      <label htmlFor="photo-upload" className="cursor-pointer bg-rose-100 text-rose-700 font-semibold py-2 px-4 rounded-lg hover:bg-rose-200 transition-colors">Upload Photo</label>
                      <input type="file" id="photo-upload" onChange={handleFileChange} className="hidden" accept="image/*" />
                      <p className="text-xs text-gray-500 mt-2 text-center">Required for face recognition. Use a clear, front-facing photo.</p>
                  </div>
                  <div className="md:col-span-2">
                      <div className="mb-8">
                          <h3 className="text-lg font-bold text-rose-600 border-b pb-2 mb-4">Personal Details</h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                              <div><label htmlFor="fullName" className="block text-sm font-medium text-gray-700">Full Name</label><input type="text" id="fullName" value={formData.fullName} onChange={handleChange} className="mt-1 block w-full bg-gray-50 p-3 rounded-md" placeholder="Ali Hassan" required /></div>
                              <div><label htmlFor="fatherName" className="block text-sm font-medium text-gray-700">Father's Name</label><input type="text" id="fatherName" value={formData.fatherName} onChange={handleChange} className="mt-1 block w-full bg-gray-50 p-3 rounded-md" placeholder="Muhammad Ashraf" required /></div>
                              <div><label htmlFor="cnic" className="block text-sm font-medium text-gray-700">CNIC</label><input type="text" id="cnic" value={formData.cnic} onChange={handleChange} className="mt-1 block w-full bg-gray-50 p-3 rounded-md" placeholder="35202-8486355-7" pattern="^\d{5}-\d{7}-\d$" inputMode="numeric" required /></div>
                              <div><label htmlFor="dob" className="block text-sm font-medium text-gray-700">Date of Birth</label><input type="date" id="dob" value={formData.dob} onChange={handleChange} className="mt-1 block w-full bg-gray-50 p-3 rounded-md" required /></div>
                          </div>
                      </div>
                      <div>
                          <h3 className="text-lg font-bold text-rose-600 border-b pb-2 mb-4">Job & Financial Details</h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                              <div><label htmlFor="employeeId" className="block text-sm font-medium text-gray-700">Employee ID</label><input type="text" id="employeeId" value={formData.employeeId} className="mt-1 block w-full bg-gray-200 p-3 rounded-md font-medium text-gray-500" readOnly /></div>
                              <div><label htmlFor="designation" className="block text-sm font-medium text-gray-700">Designation</label><input type="text" id="designation" value={formData.designation} onChange={handleChange} className="mt-1 block w-full bg-gray-50 p-3 rounded-md" placeholder="Sales Executive" required /></div>
                              <div><label htmlFor="stationId" className="block text-sm font-medium text-gray-700">Station</label><input type="text" id="stationId" value={allStations.find(s=>s.id === currentStationId)?.name || ''} className="mt-1 block w-full p-3 bg-gray-200 rounded-md" readOnly/></div>
                              <div><label htmlFor="joiningDate" className="block text-sm font-medium text-gray-700">Joining</label><input type="date" id="joiningDate" value={formData.joiningDate} onChange={handleChange} className="mt-1 block w-full bg-gray-50 p-3 rounded-md" required /></div>
                              <div><label htmlFor="phone" className="block text-sm font-medium text-gray-700">Phone</label><input type="tel" id="phone" value={formData.phone} onChange={handleChange} className="mt-1 block w-full bg-gray-50 p-3 rounded-md" placeholder="0314-5505585" pattern="^03\d{2}-\d{7}$" inputMode="numeric" required /></div>
                              <div>
                                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email</label>
                                  <div className="relative mt-1">
                                      <input type="email" id="email" value={formData.email} onChange={handleChange} className="block w-full bg-gray-50 p-3 rounded-md pr-10" placeholder="name@example.com" required />
                                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                          {isEmailValid ? (
                                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-600" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8.25 8.25a1 1 0 01-1.414 0l-3.75-3.75a1 1 0 011.414-1.414l3.043 3.043 7.543-7.543a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                                          ) : null}
                                      </div>
                                  </div>
                              </div>
                              <div><label htmlFor="salary" className="block text-sm font-medium text-gray-700">Salary (PKR)</label><input type="number" id="salary" value={formData.salary} onChange={handleChange} className="mt-1 block w-full bg-gray-50 p-3 rounded-md" placeholder="50000" min="0" required /></div>
                          </div>
                      </div>
                      <div className="mt-8">
                         <h3 className="text-lg font-bold text-rose-600 border-b pb-2 mb-4">Duty Shifts</h3>
                         {formData.dutyShifts.map((shift, index) => (
                            <div key={index} className="grid grid-cols-1 md:grid-cols-5 gap-4 items-center mb-2">
                                <div className="md:col-span-2"><label className="text-sm">Start</label><input type="time" value={shift.start} onChange={(e) => handleShiftChange(index, 'start', e.target.value)} className="w-full p-2 border rounded-md" required/></div>
                                <div className="md:col-span-2"><label className="text-sm">End</label><input type="time" value={shift.end} onChange={(e) => handleShiftChange(index, 'end', e.target.value)} className="w-full p-2 border rounded-md" required/></div>
                                {formData.dutyShifts.length > 1 && <div className="md:col-span-1"><button type="button" onClick={() => removeShift(index)} className="w-full bg-red-500 text-white p-2 rounded-md mt-4">X</button></div>}
                            </div>
                         ))}
                         <button type="button" onClick={addShift} className="mt-2 text-sm bg-gray-200 py-1 px-3 rounded-md">+ Add Shift</button>
                      </div>
                      <div className="mt-8">
                         <h3 className="text-lg font-bold text-rose-600 border-b pb-2 mb-4">Documents</h3>
                         <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">CV (PDF)</label>
                                <input type="file" accept="application/pdf" onChange={handleCvChange} className="mt-1 block w-full bg-gray-50 p-3 rounded-md" required={!employeeToEdit && !existingCvURL} />
                                {existingCvURL && !cvFile ? <a href={resolveFileUrl(existingCvURL)} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">View Current CV</a> : null}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">ID Card Front (Image)</label>
                                <input type="file" accept="image/*" onChange={handleIdCardFrontChange} className="mt-1 block w-full bg-gray-50 p-3 rounded-md" required={!employeeToEdit && !existingIdCardFrontURL} />
                                {existingIdCardFrontURL && !idCardFrontFile ? <a href={resolveFileUrl(existingIdCardFrontURL)} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">View Current Front</a> : null}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">ID Card Back (Image)</label>
                                <input type="file" accept="image/*" onChange={handleIdCardBackChange} className="mt-1 block w-full bg-gray-50 p-3 rounded-md" required={!employeeToEdit && !existingIdCardBackURL} />
                                {existingIdCardBackURL && !idCardBackFile ? <a href={resolveFileUrl(existingIdCardBackURL)} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">View Current Back</a> : null}
                            </div>
                         </div>
                      </div>
                  </div>
              </div>
              <div className="mt-12 pt-6 border-t border-gray-200 flex justify-end">
                  <button type="submit" disabled={isSubmitting} className="bg-rose-600 text-white font-bold py-3 px-8 rounded-lg shadow-lg hover:bg-rose-700 transition-all disabled:opacity-50">
                      {isSubmitting ? 'Saving...' : (employeeToEdit ? 'Save Changes' : 'Register Employee')}
                  </button>
              </div>
          </form>
      </div>
    );
}

function EmployeeDetailView({ employee, onClose }) {
    const { allStations } = useAppContext();
    if (!employee) return null;
    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex justify-center items-center p-4 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full relative overflow-hidden transform transition-all duration-300">
                <div className="bg-gradient-to-r from-rose-700 to-red-800 h-28"></div>
                <button onClick={onClose} className="absolute top-4 right-4 p-2 bg-black/20 text-white rounded-full hover:bg-black/40">
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
                <div className="p-8">
                    <div className="flex flex-col md:flex-row items-center md:items-end -mt-24">
                        <img src={employee.photoURL ? resolveFileUrl(employee.photoURL) : 'https://placehold.co/160x160/E2E8F0/4A5568?text=Photo'} alt={employee.fullName} className="h-40 w-40 rounded-full object-cover border-8 border-white shadow-lg" onError={(e) => { e.target.src="https://placehold.co/160x160/E2E8F0/4A5568?text=Photo"; }}/>
                        <div className="md:ml-6 mt-4 md:mt-0 text-center md:text-left">
                            <h2 className="text-4xl font-bold text-gray-900">{employee.fullName}</h2>
                            <p className="text-lg text-rose-600 font-semibold">{employee.designation}</p>
                            <p className="text-sm text-gray-500">Employee ID: {employee.employeeId}</p>
                        </div>
                    </div>
                    <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div>
                           <h3 className="text-xl font-bold text-gray-800 border-b-2 border-rose-200 pb-2 mb-4">Job Details</h3>
                           <div className="space-y-3 text-gray-700">
                               <p><strong>Station:</strong> {allStations.find(s => s.id === employee.stationId)?.name || 'N/A'}</p>
                               <p><strong>Joining Date:</strong> {employee.joiningDate}</p>
                               <p><strong>Salary:</strong> PKR {(parseFloat(employee.salary) || 0).toLocaleString()}</p>
                               <div><strong>Duty Shifts:</strong><ul className="list-disc list-inside ml-4 mt-1">{(typeof employee.dutyShifts === 'string' ? JSON.parse(employee.dutyShifts) : employee.dutyShifts || []).map((shift, i) => (<li key={i}>{shift.start} - {shift.end}</li>))}</ul></div>
                           </div>
                        </div>
                         <div>
                           <h3 className="text-xl font-bold text-gray-800 border-b-2 border-rose-200 pb-2 mb-4">Personal Information</h3>
                           <div className="space-y-3 text-gray-700">
                               <p><strong>Father's Name:</strong> {employee.fatherName}</p>
                               <p><strong>CNIC:</strong> {employee.cnic}</p>
                               <p><strong>Date of Birth:</strong> {employee.dob}</p>
                               <p><strong>Phone:</strong> {employee.phone}</p>
                               <p><strong>Email:</strong> {employee.email || 'N/A'}</p>
                           </div>
                        </div>
                        <div className="md:col-span-2">
                           <h3 className="text-xl font-bold text-gray-800 border-b-2 border-rose-200 pb-2 mb-4">Documents</h3>
                           <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                               <div className="bg-gray-50 border rounded-lg p-4">
                                   <div className="font-semibold text-gray-700 mb-2">CV</div>
                                   {employee.cvURL ? <a href={resolveFileUrl(employee.cvURL)} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-semibold">View PDF</a> : <div className="text-sm text-gray-500">N/A</div>}
                               </div>
                               <div className="bg-gray-50 border rounded-lg p-4">
                                   <div className="font-semibold text-gray-700 mb-2">ID Card Front</div>
                                   {employee.idCardFrontURL ? <a href={resolveFileUrl(employee.idCardFrontURL)} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-semibold">View Image</a> : <div className="text-sm text-gray-500">N/A</div>}
                               </div>
                               <div className="bg-gray-50 border rounded-lg p-4">
                                   <div className="font-semibold text-gray-700 mb-2">ID Card Back</div>
                                   {employee.idCardBackURL ? <a href={resolveFileUrl(employee.idCardBackURL)} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-semibold">View Image</a> : <div className="text-sm text-gray-500">N/A</div>}
                               </div>
                           </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// --- Main Component: EmployeeRegistrationSection ---
function EmployeeRegistrationSection() {
    const { user, currentStationId } = useAppContext();
    const [employees, setEmployees] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [view, setView] = useState('list'); // 'list', 'form', 'detail'
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [deletingEmployee, setDeletingEmployee] = useState(null);

    const fetchEmployees = useCallback(async () => {
        if (!currentStationId || currentStationId === 'all') {
            setEmployees([]); setIsLoading(false); return;
        }
        setIsLoading(true);
        try {
            const data = await api.get('workers', { where: { stationId: currentStationId } });
            setEmployees(data);
        } catch (err) {
            toast.error("Failed to fetch employees.");
        } finally {
            setIsLoading(false);
        }
    }, [currentStationId]);

    useEffect(() => {
        fetchEmployees();
    }, [fetchEmployees]);

    const handleEdit = (employee) => {
        setSelectedEmployee(employee);
        setView('form');
    };

    const handleViewDetails = (employee) => {
        setSelectedEmployee(employee);
        setView('detail');
    };

    const handleBackToList = () => {
        setView('list');
        setSelectedEmployee(null);
        fetchEmployees();
    };

    const handleDelete = async () => {
        if (!deletingEmployee) return;
        try {
            await api.delete('workers', deletingEmployee.id);
            toast.success("Employee deleted successfully.");
            fetchEmployees();
        } catch (err) {
            toast.error("Failed to delete employee.");
        } finally {
            setDeletingEmployee(null);
        }
    };

    if (view === 'form') {
        return <EmployeeForm employeeToEdit={selectedEmployee} onBack={handleBackToList} />;
    }

    if (view === 'detail') {
        return <EmployeeDetailView employee={selectedEmployee} onClose={handleBackToList} />;
    }

    return (
        <div className="container mx-auto">
            <ConfirmationModal isOpen={!!deletingEmployee} onClose={() => setDeletingEmployee(null)} onConfirm={handleDelete} title="Delete Employee">
                Are you sure you want to delete <strong>{deletingEmployee?.fullName}</strong>? This action is irreversible.
            </ConfirmationModal>

            <div className="flex justify-between items-center mb-8">
                <div>
                    <h1 className="text-4xl font-bold text-gray-800">Employee Management</h1>
                    <p className="text-gray-500 mt-1">Register new employees and manage existing staff.</p>
                </div>
                <button onClick={() => { setSelectedEmployee(null); setView('form'); }} className="bg-rose-600 text-white font-bold py-3 px-6 rounded-lg shadow-lg hover:bg-rose-700 transition-all">
                    + Add New Employee
                </button>
            </div>

            {currentStationId === 'all' ? (
                <div className="text-center p-10 bg-white rounded-2xl shadow-lg">
                    <h3 className="text-xl font-bold text-gray-700">Please select a station</h3>
                    <p className="text-gray-500 mt-2">Select a station from the sidebar to view and manage employees.</p>
                </div>
            ) : isLoading ? (
                <p>Loading employees...</p>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                    {employees.map(emp => (
                        <div key={emp.id} className="bg-white rounded-2xl shadow-lg overflow-hidden text-center group">
                            <div className="py-6">
                                <img src={emp.photoURL ? resolveFileUrl(emp.photoURL) : 'https://placehold.co/128x128/E2E8F0/4A5568?text=Photo'} alt={emp.fullName} className="h-32 w-32 rounded-full object-cover mx-auto border-4 border-gray-200 group-hover:border-rose-500 transition-all" onError={(e) => { e.target.src="https://placehold.co/128x128/E2E8F0/4A5568?text=Photo"; }}/>
                                <h3 className="mt-4 text-lg font-bold text-gray-800">{emp.fullName}</h3>
                                <p className="text-sm text-rose-600 font-semibold">{emp.designation}</p>
                                <p className="text-xs text-gray-400">ID: {emp.employeeId}</p>
                            </div>
                            <div className="bg-gray-50 px-4 py-3 flex justify-center gap-2 border-t">
                                <button onClick={() => handleViewDetails(emp)} className="text-sm font-semibold text-gray-600 hover:text-rose-600">Details</button>
                                <span className="text-gray-300">|</span>
                                <button onClick={() => handleEdit(emp)} className="text-sm font-semibold text-gray-600 hover:text-rose-600">Edit</button>
                                {user.role === 'admin' && (
                                    <>
                                        <span className="text-gray-300">|</span>
                                        <button onClick={() => setDeletingEmployee(emp)} className="text-sm font-semibold text-red-500 hover:text-red-700">Delete</button>
                                    </>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default EmployeeRegistrationSection;
